/**
 * Health Score Indicator Component
 * Displays client health score with color-coded progress bar
 */

import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { Favorite as HeartIcon } from '@mui/icons-material';

interface HealthScoreIndicatorProps {
  score: number; // 0-100
  showLabel?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#4caf50'; // Green
  if (score >= 60) return '#8bc34a'; // Light Green
  if (score >= 40) return '#ff9800'; // Orange
  if (score >= 20) return '#ff5722'; // Deep Orange
  return '#f44336'; // Red
};

const getScoreLabel = (score: number): string => {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Fair';
  if (score >= 20) return 'Poor';
  return 'Critical';
};

export const HealthScoreIndicator: React.FC<HealthScoreIndicatorProps> = ({
  score,
  showLabel = true,
  size = 'medium'
}) => {
  const color = getScoreColor(score);
  const label = getScoreLabel(score);
  const height = size === 'small' ? 6 : size === 'large' ? 12 : 8;

  return (
    <Box sx={{ width: '100%' }}>
      {showLabel && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <HeartIcon sx={{ fontSize: 16, color }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color }}>
              {label}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {score}/100
          </Typography>
        </Box>
      )}
      <LinearProgress
        variant="determinate"
        value={score}
        sx={{
          height,
          borderRadius: 1,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 1
          }
        }}
      />
    </Box>
  );
};

export default HealthScoreIndicator;
